import { Outlet, createRootRouteWithContext } from "@tanstack/react-router";
import * as Sentry from "@sentry/react";
import { useQuery, type QueryClient } from "@tanstack/react-query";
import { client } from "@/lib/client/client.gen";
import {
	getLevelsOptions,
	getSchoolsOptions,
	getSubjectsOptions,
	getTimeSlotsOptions,
} from "@/lib/client/@tanstack/react-query.gen";
import { useEffect } from "react";
import {
	setLevels,
	setSchools,
	setSubjects,
	setTimeSlots,
} from "@/stores/enum-store";
import { Toaster } from "sonner";
import { clearAuth } from "@/stores/auth.store";

interface MyRouterContext {
	queryClient: QueryClient;
}

let interceptorInitialized = false;

export const Route = createRootRouteWithContext<MyRouterContext>()({
	beforeLoad: () => {
		client.setConfig({
			auth: localStorage.getItem("token") || undefined,
			baseUrl: import.meta.env.VITE_API_URL,
		});

		if (!interceptorInitialized) {
			interceptorInitialized = true;

			client.interceptors.response.use(async (response) => {
				if (response.status === 401 || response.status === 403) {
					clearAuth();
					window.location.href = "/login";
				}
				return response;
			});
		}
	},
	component: () => {
		const { data: subjects, isLoading: isSubjectLoading } = useQuery(
			getSubjectsOptions(),
		);
		const { data: timeslots, isLoading: isTimeslotsLoading } = useQuery(
			getTimeSlotsOptions(),
		);
		const { data: levels, isLoading: islevelsLoading } = useQuery(
			getLevelsOptions(),
		);
		const { data: schools, isLoading: isSchoolsLoading } = useQuery(
			getSchoolsOptions(),
		);

		useEffect(() => {
			setSchools(schools || []);
			setSubjects(subjects || []);
			setLevels(levels || []);
			setTimeSlots(timeslots || []);
		}, [subjects, timeslots, levels, schools]);

		if (
			isSchoolsLoading &&
			isSubjectLoading &&
			isTimeslotsLoading &&
			islevelsLoading
		) {
			return <>Chargement...</>;
		}

		return (
			<Sentry.ErrorBoundary
				fallback={({ resetError }) => (
					<div className="p-4 bg-red-100 text-red-800">
						<h1>Une erreur est survenue</h1>
						<p>Oups, une erreur est survenue. Notre équipe est déjà sur le coup !</p>
         				<button onClick={resetError}>Réessayer</button>
					</div>
				)}
			>
				<Toaster />
				<Outlet />
			</Sentry.ErrorBoundary>
		);
	},
});
